import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { AlertTriangle, ArrowUpRight, Clock, ShieldCheck } from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { ReasonDialog } from "@/components/ReasonDialog"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { daysUntil, formatAmount, formatDate, formatPeriodShort } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import { FILING_STATUS_LABELS, type Filing } from "@/lib/types"
import { useTableKeyboardNav } from "@/lib/useTableKeyboardNav"
import { cn } from "@/lib/utils"

import { onWriteError, reportWriteError } from "@/lib/writeErrors"

import { FilingDetailDialog } from "@/routes/accountant/FilingDetailDialog"
import {
  descargarArchivoDeclaracion,
  fetchDeclaracion,
  fetchFilingDetail,
  fetchFilings,
  observeFiling,
  signFiling,
} from "@/routes/accountant/api"

const STATUS_VARIANT: Record<
  Filing["status"],
  "success-soft" | "warning-soft" | "outline" | "secondary" | "brand-soft"
> = {
  en_preparacion: "outline",
  listo_para_revision: "brand-soft",
  observado: "warning-soft",
  firmado: "success-soft",
  presentado: "secondary",
}

/**
 * Vencimiento con cuenta regresiva.
 *
 * La fecha sola no sirve: lo que decide el orden del dia es cuantos dias
 * quedan. Vencido en rojo y en negrita, hoy/manana en ambar, el resto apagado.
 * La barra a la izquierda repite el estado sin depender del color.
 */
function DueCell({ dueDate }: { dueDate: string }) {
  // El backend no devuelve vencimiento: `period` no lo guarda y el cronograma de
  // SUNAT existe en el servidor pero no esta expuesto por HTTP (ver
  // accountant/mapping.ts). Sin fecha, `daysUntil("")` da NaN y la celda decia
  // "en NaNd". Se dice lo que pasa: no hay cronograma.
  if (!dueDate) {
    return (
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="h-4 w-[2px] rounded-full bg-border" />
        <span
          className="text-2xs text-muted-foreground/70"
          title={t("El backend todavia no expone el cronograma de vencimientos")}
        >
          {t("sin cronograma")}</span>
      </div>
    )
  }

  const days = daysUntil(dueDate)
  const level = days < 0 ? "vencido" : days <= 3 ? "urgente" : "normal"
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          "h-4 w-[2px] rounded-full",
          level === "vencido"
            ? "bg-destructive"
            : level === "urgente"
              ? "bg-warning"
              : "bg-border-strong",
        )}
      />
      <span className="code text-muted-foreground">{formatDate(dueDate)}</span>
      <span
        className={cn(
          "text-2xs tabular-nums",
          level === "vencido"
            ? "font-semibold text-destructive"
            : level === "urgente"
              ? "font-medium text-warning"
              : "text-muted-foreground",
        )}
      >
        {days < 0 ? `vencido ${Math.abs(days)}d` : days === 0 ? "hoy" : `en ${days}d`}
      </span>
    </div>
  )
}

export function AccountantConsolePage() {
  const { profile } = useAuth()
  const { can, memberships } = useRbac()
  const { term } = useI18n()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [statusFilter, setStatusFilter] = React.useState("todas")
  const [selected, setSelected] = React.useState<Filing | null>(null)
  const [signOpen, setSignOpen] = React.useState(false)
  const [observeOpen, setObserveOpen] = React.useState(false)
  const [signedIds, setSignedIds] = React.useState<Record<string, boolean>>({})

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["filings"],
    queryFn: fetchFilings,
  })

  /**
   * El detalle del periodo abierto: excepciones reales, cuadre por libro y diff.
   *
   * Se dispara al SELECCIONAR una fila, no al cargar la bandeja: recorre
   * `journal_line` del periodo y del anterior, y el contador abre una fila a la
   * vez. La clave lleva el id, asi que cambiar de fila cambia de cache y no
   * quedan datos de la declaracion anterior en pantalla.
   *
   * Va con el tenant del PERIODO. `/filings/inbox` es la unica ruta
   * cross-tenant; el detalle no lo es, y con el tenant activo daria 403 en toda
   * fila de otra empresa de la cartera.
   */
  const detailQuery = useQuery({
    queryKey: ["filing-detail", selected?.id, selected?.tenantId],
    queryFn: () => fetchFilingDetail(selected!.id, selected!.tenantId),
    enabled: Boolean(selected),
    staleTime: 30_000,
  })
  // Sin `placeholderData`, un cambio de clave devuelve `undefined` mientras la
  // nueva carga: nunca se ve el cuadre de la declaracion anterior sobre la
  // recien abierta. Es justo lo que se quiere aqui — mostrar el cuadre de otro
  // periodo en la pantalla donde se firma seria peor que mostrar un esqueleto.
  const detail = detailQuery.data ?? null

  /**
   * LA DECLARACION ARMADA. Va aparte del detalle y con su propia clave porque
   * es otra pregunta y cuesta otra cosa: el detalle cuadra los libros del
   * periodo, esto CONSTRUYE los formularios —normaliza cada comprobante, lee su
   * tratamiento guardado y genera el TXT del PLE—. Mezclarlas haria que abrir
   * la bandeja armara declaraciones que nadie pidio.
   */
  const declaracionQuery = useQuery({
    queryKey: ["filing-declaracion", selected?.id, selected?.tenantId],
    queryFn: () => fetchDeclaracion(selected!.id, selected!.tenantId),
    enabled: Boolean(selected),
    staleTime: 30_000,
  })
  const declaracion = declaracionQuery.data ?? null

  // El tenant de la fila, NO el activo: `/filings/inbox` es cross-tenant y las
  // escrituras piden el tenant del periodo. Con el activo, firmar cualquier fila
  // de otra empresa de la cartera daria 403.
  const signMutation = useMutation({
    mutationFn: (reason: string) =>
      signFiling(selected?.id as string, reason, selected?.tenantId),
    onSuccess: () => {
      if (selected) setSignedIds((prev) => ({ ...prev, [selected.id]: true }))
      toast({
        variant: "success",
        title: "Declaracion firmada",
        description: "Periodo cerrado. La firma queda en el audit log con su motivo.",
      })
      setSignOpen(false)
      setSelected(null)
      void queryClient.invalidateQueries({ queryKey: ["filings"] })
    },
    onError: onWriteError("la declaracion", "firmar"),
  })

  const observeMutation = useMutation({
    mutationFn: (reason: string) =>
      observeFiling(selected?.id as string, reason, selected?.tenantId),
    onSuccess: () => {
      toast({
        variant: "warning",
        title: "Declaracion observada",
        description:
          "Vuelve a preparacion con tu motivo. El backend no tiene estado 'observado': el periodo queda abierto y la observacion, en el audit log.",
      })
      setObserveOpen(false)
      setSelected(null)
      void queryClient.invalidateQueries({ queryKey: ["filings"] })
    },
    onError: onWriteError("la declaracion", "observar"),
  })

  const filings = React.useMemo(
    () =>
      (data ?? [])
        .map((filing) =>
          signedIds[filing.id]
            ? { ...filing, status: "firmado" as const, signedBy: profile?.displayName }
            : filing,
        )
        .filter((filing) => (statusFilter === "todas" ? true : filing.status === statusFilter))
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [data, signedIds, statusFilter, profile?.displayName],
  )

  const pending = filings.filter(
    (filing) => filing.status !== "firmado" && filing.status !== "presentado",
  )
  const overdue = pending.filter((filing) => daysUntil(filing.dueDate) < 0)
  const blocked = pending.filter(
    (filing) =>
      !filing.balanced ||
      filing.exceptions.some((exception) => !exception.resolved && exception.severity === "alta"),
  )

  const nav = useTableKeyboardNav({
    count: filings.length,
    onActivate: (index) => setSelected(filings[index] ?? null),
  })

  /**
   * Por que NO se puede firmar. Es una comprobacion de cortesia, no el control:
   * el control esta en el backend (`exigir_firma` + excepciones bloqueantes) y
   * responde 403 / 409, que ahora se ven en pantalla via `onWriteError`.
   *
   * La separacion de funciones en particular NO puede resolverse aqui: el
   * backend devuelve `cerrado_por` como uuid de `app_user` y la SPA solo conoce
   * el nombre para mostrar, asi que la comparacion nunca coincide. Se deja
   * porque funciona con los mocks y porque el dia que el backend devuelva el
   * nombre empieza a servir; mientras tanto, quien prepara y firma recibe el 403
   * del servidor con su explicacion.
   */
  function signBlockedReason(filing: Filing | null) {
    if (!filing) return undefined
    if (!can("sign", filing.tenantId)) return "Tu rol no tiene capacidad de firma en este tenant."
    if (filing.preparedBy && filing.preparedBy === profile?.displayName)
      return "Separacion de funciones: quien prepara no puede firmar."

    // El DETALLE manda cuando esta: la bandeja lee `balanced: true` y las
    // excepciones a medias porque no puede recorrer los asientos de toda la
    // cartera. Mientras carga se bloquea la firma —no se habilita un boton
    // apoyandose en un cuadre que todavia no se comprobo— y si la consulta
    // fallo, tambien: el backend rechazaria igual, y aqui se dice por que.
    const esteDetalle = selected?.id === filing.id ? detail : null
    if (!esteDetalle) {
      if (detailQuery.isError) return "No se pudo cargar el detalle del periodo: no se puede comprobar el cuadre."
      return "Comprobando el cuadre y las excepciones del periodo…"
    }
    if (esteDetalle.hasEntries && !esteDetalle.balanced)
      return "Los libros del periodo no cuadran: no se puede firmar."
    if (
      esteDetalle.exceptions.some(
        (exception) => !exception.resolved && exception.severity === "alta",
      )
    )
      return "Hay excepciones de severidad alta sin resolver."
    return undefined
  }

  const canSignSelected = Boolean(selected) && !signBlockedReason(selected)

  return (
    <div className="space-y-3">
      <PageHeader
        eyebrow="Cartera del contador"
        title={t("Consola de declaraciones")}
        description={`Bandeja cross-tenant ordenada por vencimiento. ${memberships.length} entidades en tu cartera. La firma cierra el periodo: despues de cerrado, las correcciones van por asiento de reversion en un periodo posterior.`}
        actions={
          <>
            <Badge variant="outline">
              <Clock />
              {pending.length} pendientes
            </Badge>
            {blocked.length > 0 ? (
              <Badge variant="warning-soft" title={t("No cuadran o tienen excepciones altas abiertas")}>
                <ShieldCheck />
                {blocked.length} bloqueadas
              </Badge>
            ) : null}
            {overdue.length > 0 ? (
              <Badge variant="destructive">
                <AlertTriangle />
                {overdue.length} vencidas
              </Badge>
            ) : null}
          </>
        }
      />

      <Toolbar>
        <ToolbarField label={t("Estado")} htmlFor="f-estado">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger id="f-estado" className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">{t("Todos los estados")}</SelectItem>
              <SelectItem value="en_preparacion">{t("En preparacion")}</SelectItem>
              <SelectItem value="listo_para_revision">{t("Listo para revision")}</SelectItem>
              <SelectItem value="observado">{t("Observado")}</SelectItem>
              <SelectItem value="firmado">{t("Firmado")}</SelectItem>
            </SelectContent>
          </Select>
        </ToolbarField>

        {/*
          LOS SALDOS ARRASTRADOS, A UN CLIC. La pantalla existía
          (`/contabilidad/arrastres`) y el backend mandaba a ella («Cárgalo en
          Declaraciones › Saldos arrastrados»), pero no había ni pestaña ni
          enlace: no se podía llegar. Sin ellos cinco casillas del formulario
          salen en cero, y un cero ahí no descuadra nada.
        */}
        <Button asChild variant="outline" size="sm" className="self-end">
          <Link to="/contabilidad/arrastres">
            <ArrowUpRight />
            {t("Saldos arrastrados")}
          </Link>
        </Button>

        <p className="ml-auto flex max-w-[46ch] items-start gap-1.5 pb-1 text-2xs leading-4 text-muted-foreground">
          <ShieldCheck className="mt-px h-3 w-3 shrink-0" />
          {t("Separacion de funciones: quien prepara una declaracion no puede firmarla. El sistema lo impide, no lo sugiere.")}</p>
      </Toolbar>

      <WorkPanel
        title={term.declaracion_mensual}
        meta={
          <Badge variant="outline" mono>
            {filings.length}
          </Badge>
        }
        footnote={t("Enter sobre una fila abre el detalle: excepciones, diff contra el mes anterior y el bloqueo de firma si lo hubiera.")}
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={filings.length === 0}
          emptyMessage={
            statusFilter === "todas"
              ? "No hay declaraciones en tu cartera."
              : "Ninguna declaracion esta en ese estado."
          }
          emptyHint={
            statusFilter === "todas"
              ? "Cuando el motor cierre el primer periodo de alguna entidad, la declaracion aparecera aqui lista para revisar."
              : "Cambia el filtro de estado para ver el resto de la cartera."
          }
          columns={[12, 20, 10, 8, 14, 10, 8, 8, 6, 6]}
        >
          <Table containerClassName="max-h-[calc(100vh-20rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">{t("Vencimiento")}</TableHead>
                <TableHead className="min-w-[13rem]">{t("Entidad")}</TableHead>
                <TableHead className="w-32">{term.identificador_fiscal}</TableHead>
                <TableHead className="w-24">{t("Periodo")}</TableHead>
                <TableHead className="w-52">{t("Formulario")}</TableHead>
                <TableHead className="w-36">{t("Estado")}</TableHead>
                <TableHead className="w-32 text-right">{term.impuesto_ventas}{" "}{t("a pagar")}</TableHead>
                <TableHead className="w-32 text-right">{t("Pago a cuenta")}</TableHead>
                <TableHead className="w-24 text-right">{t("Excepc.")}</TableHead>
                <TableHead className="w-24">{t("Cuadre")}</TableHead>
                <TableHead className="w-28">{t("Preparado por")}</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody {...nav.bodyProps}>
              {filings.map((filing, index) => {
                const openExceptions = filing.exceptions.filter((exception) => !exception.resolved)
                const highOpen = openExceptions.filter(
                  (exception) => exception.severity === "alta",
                ).length
                return (
                  <TableRow
                    key={filing.id}
                    {...nav.rowProps(index)}
                    className={cn(
                      "outline-none",
                      nav.cursor === index && "row-cursor",
                      !filing.balanced && "bg-destructive-soft/60 hover:bg-destructive-soft",
                    )}
                  >
                    <TableCell>
                      <DueCell dueDate={filing.dueDate} />
                    </TableCell>
                    <TableCell className="truncate font-medium" title={filing.tenantName}>
                      {filing.tenantName}
                    </TableCell>
                    <TableCell className="code text-muted-foreground">{filing.ruc}</TableCell>
                    <TableCell className="text-2xs text-muted-foreground">
                      {formatPeriodShort(filing.period)}
                    </TableCell>
                    <TableCell className="truncate text-2xs" title={filing.form}>
                      {filing.form}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[filing.status]}>
                        {FILING_STATUS_LABELS[filing.status]}
                      </Badge>
                      {filing.signedBy ? (
                        <div className="mt-px truncate text-2xs text-muted-foreground">
                          firmo {filing.signedBy}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="num-strong">{formatAmount(filing.igvAPagar)}</TableCell>
                    <TableCell className="num">{formatAmount(filing.pagoACuenta)}</TableCell>
                    <TableCell className="num">
                      {openExceptions.length > 0 ? (
                        <span
                          className={cn(
                            "font-semibold",
                            highOpen > 0 ? "text-destructive" : "text-warning",
                          )}
                          title={
                            highOpen > 0
                              ? `${highOpen} de severidad alta sin resolver: bloquean la firma`
                              : "Excepciones abiertas de severidad media o baja"
                          }
                        >
                          {openExceptions.length}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">0</span>
                      )}
                      <span className="text-2xs text-muted-foreground">
                        /{filing.exceptions.length}
                      </span>
                    </TableCell>
                    <TableCell>
                      {filing.balanced ? (
                        <Badge variant="success-soft">cuadra</Badge>
                      ) : (
                        <Badge variant="destructive">{t("no cuadra")}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="truncate text-2xs text-muted-foreground">
                      {filing.preparedBy}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="xs"
                        variant="outline"
                        className="opacity-70 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100"
                        onClick={() => setSelected(filing)}
                      >
                        {t("Revisar")}</Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      <FilingDetailDialog
        filing={selected}
        detail={detail}
        detailLoading={detailQuery.isLoading}
        detailError={detailQuery.error}
        onRetryDetail={() => void detailQuery.refetch()}
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
        periodId={selected?.id}
        periodTenantId={selected?.tenantId}
        canSign={canSignSelected}
        signBlockedReason={signBlockedReason(selected)}
        onSign={() => setSignOpen(true)}
        onObserve={() => setObserveOpen(true)}
        declaracion={declaracion}
        declaracionLoading={declaracionQuery.isLoading}
        declaracionError={declaracionQuery.error}
        onRetryDeclaracion={() => void declaracionQuery.refetch()}
        onDescargar={(filingCode, nombre) => {
          if (!selected) return
          void descargarArchivoDeclaracion(selected.id, filingCode, nombre, selected.tenantId).catch(
            (error: unknown) =>
              reportWriteError(error, {
                entidad: "el archivo de la declaración",
                operacion: "descargar",
              }),
          )
        }}
      />

      <ReasonDialog
        open={signOpen}
        onOpenChange={setSignOpen}
        title={t("Firmar declaracion y cerrar periodo")}
        description={t("La firma es un paso obligatorio y auditado, no un checkbox.")}
        summary={
          selected ? (
            <div className="space-y-0.5">
              <div className="font-medium text-foreground">{selected.tenantName}</div>
              <div className="text-muted-foreground">
                {selected.form} · {formatPeriodShort(selected.period)}
              </div>
              <div className="pt-1">
                {term.impuesto_ventas}{" "}{t("a pagar")}{" "}
                <strong className="code text-sm">{formatAmount(selected.igvAPagar)}</strong>
              </div>
            </div>
          ) : null
        }
        confirmLabel={t("Firmar y cerrar")}
        suggestions={[
          "Revisadas las excepciones y el diff contra el mes anterior; conforme.",
          "Verificado el cuadre del balance y los libros de respaldo.",
        ]}
        pending={signMutation.isPending}
        onConfirm={(reason) => signMutation.mutateAsync(reason).then(() => undefined)}
      />

      <ReasonDialog
        open={observeOpen}
        onOpenChange={setObserveOpen}
        title={t("Observar declaracion")}
        description={t("Vuelve a preparacion con tu observacion registrada.")}
        variant="destructive"
        confirmLabel={t("Observar")}
        pending={observeMutation.isPending}
        onConfirm={(reason) => observeMutation.mutateAsync(reason).then(() => undefined)}
      />
    </div>
  )
}
